<template>
	<div class="application-skills">
		<div class="form-icon-input">
			<div>
				<Icon icon="search"/>
			</div>
			<div>
				<VueSimpleSuggest
					v-model="keyword"
					:placeholder="computedKeywordPlaceholder"
					:list="filteredItems"
					:min-length="2"
					value-attribute="id"
					display-attribute="suggestion"
					:styles="autoCompleteStyle"
					:max-suggestions="0"
					:debounce="200"
					:prevent-submit="false"
					ref="autocomplete"
					prevent-hide
					@input="keywordChanged"
					@keyup.native="onEnter"
					@suggestion-click="itemClicked"
				/>
			</div>
		</div>
		<div v-if="!hasSelectedItems" class="application-skills--none">
			<b-form-checkbox v-model="none" @change="emitChangeEvent">
				{{ noneText }}
			</b-form-checkbox>
		</div>
		<div v-if="!none" class="application-skills--items">
			<div
				v-for="item in items"
				:key="item.id"
				class="application-skills--item"
			>
				<div>
					<span v-if="item.freeText">{{ item.freeText }}</span>
					<span v-else>
						<Label :characteristic="item.item" :label-type="labelType"/>
					</span>
				</div>
				<div>
					<div class="font-size-smaller mb-1">{{ labels.selfAssessmentTitle }}</div>
					<Select
						v-model="item.selfAssessment"
						:options="selfAssessmentOptions"
						smaller
						@input="emitChangeEvent"
					/>
				</div>
				<div>
					<a href="#" @click.prevent="remove(item)">
						<Icon icon="bin"/>
					</a>
				</div>
			</div>
		</div>
	</div>
</template>
<script>
import AutoCompleteStyle from "@/helper/style/AutoComplete";
import VueSimpleSuggest from 'vue-simple-suggest'
import {mapState} from "vuex";
import {flatten} from "@/helper/characteristic/Flatten";
import _, {debounce} from "lodash";
import {APPLICATION, get, JOB_PROFILE} from "@/helper/characteristic/Label";
import {search} from "@/helper/characteristic/Search";
import {getParents} from "@/helper/application/characteristic/Provider";
import Label from "@/vue/characteristic/Label";
import {isDefined} from "@/helper/Value";
import Icon from "@/vue/icon/Icon"
import {uuid} from "vue-uuid";
import {equals, isEmptyOrNull, trim} from "@/helper/String";
import Dropdown from "@/vue/characteristic/Dropdown";
import Scale from "@/vue/characteristic/Scale.vue";
import * as LabelSet from "@/helper/characteristic/edit/LabelSet";
import {arrayIsNotEmpty} from "@/helper/Array";
import * as Event from "@/helper/tracking/Event";
import EventData from "@/helper/tracking/EventData";
import Select from "@/vue/common/form/Select.vue";

const ADD_NEW = 'add-new';

export default {

	components: {
		Select,
		Scale,
		Dropdown,
		Icon,
		Label,
		VueSimpleSuggest,
	},

	props: {
		value: {
			type: Array,
			required: false,
		},
		type: {
			type: String,
			required: false,
			default: 'application',
		},
		noneText: {
			type: String,
			required: true,
		},
		metaSkillProperty: {
			type: String,
			required: true,
		},
		selfAssessmentPlaceholder: {
			type: String,
			required: true,
		},
		withRequirement: {
			type: Boolean,
			required: false,
			default: false,
		},
		keywordPlaceholder: {
			type: String,
			required: false,
			default: 'Suchbegriff eingeben...'
		},
		keywordPlaceholderFurther: {
			type: String,
			required: false
		},
	},

	data ()
	{
		return {
			keyword: '',
			autoCompleteStyle: AutoCompleteStyle.get(),
			none: isDefined(this.value) && this.value.length === 0,
			items: this.value || [],
		};
	},

	computed: {
		...mapState('meta', ['meta']),
		computedKeywordPlaceholder ()
		{
			return !this.hasSelectedItems
				? this.keywordPlaceholder
				: (this.keywordPlaceholderFurther || this.keywordPlaceholder);
		},
		labels ()
		{
			return LabelSet.get(
				this.type === 'application'
					? 'application'
					: 'customer'
			);
		},
		labelType ()
		{
			return this.type === 'application'
				? APPLICATION
				: JOB_PROFILE;
		},
		characteristicItems ()
		{
			return this.meta[this.metaSkillProperty];
		},
		selfAssessmentOptions ()
		{
			return _.map(this.meta.skillSelfAssessment, characteristicItem =>
			{
				return {
					value: characteristicItem,
					text: get(characteristicItem, this.labelType)
				};
			});
		},
		filteredItems (data)
		{
			const items = _
				.chain(
					flatten(this.characteristicItems)
				)
				.filter(characteristic =>
				{
					const alreadyIn = this.characteristicAlreadySelected(characteristic);

					return search(characteristic, data.keyword) && characteristic.children.length === 0 && !alreadyIn;
				})
				.sortBy('suggestion')
				.map(characteristic =>
				{
					const parents = getParents(characteristic.id, this.characteristicItems);

					let suggestion = get(characteristic);

					if (parents.length > 0)
					{
						let parentsString = _
							.chain(parents)
							.map(parent => parent.label)
							.value()
							.join(' > ');

						suggestion += ' (' + parentsString + ')';
					}

					return {
						...characteristic,
						suggestion
					};
				})
				.value();

			if (items.length === 0 && data.keyword)
			{
				items.unshift({
					id: ADD_NEW,
					suggestion: '"' + data.keyword + '" hinzufügen'
				});
			}

			return items;
		},
		validItems ()
		{
			return _.filter(
				this.items,
				item =>
				{
					const requirementValid = !this.withRequirement || isDefined(item.requirement);

					return isDefined(item.selfAssessment) && requirementValid;
				}
			);
		},
		hasSelectedItems ()
		{
			return this.items.length > 0;
		},
		valid ()
		{
			if (this.none)
			{
				return true;
			}

			if (!this.hasSelectedItems)
			{
				return false;
			}

			return this.items.length === this.validItems.length;
		},
	},

	methods: {
		logText: debounce((keyword) =>
		{
			let action = 'Suche (Software)';

			Event.push(
				new EventData()
					.setEvent(Event.TAG_SEARCH)
					.setAction(action)
					.setName(keyword)
			);

		}, 1000),
		keywordChanged ()
		{
			if (!isEmptyOrNull(this.keyword))
			{
				this.logText(this.keyword);
			}
		},
		characteristicAlreadySelected (characteristic)
		{
			return isDefined(
				_.find(this.items, item => item.item?.id === characteristic.id)
			);
		},
		onEnter (e)
		{
			e.preventDefault();

			const freeText = trim(this.keyword);

			if (e.keyCode !== 13 || freeText.length < 2) // not enter or free text too short
			{
				return;
			}

			this.addItem({freeText: this.keyword});
		},
		remove (item)
		{
			this.items = _.filter(this.items, i => i.id !== item.id);

			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction('Entfernt (Software)')
					.setName(item.item?.label ?? item.freeText)
			);

			this.emitHasItemsEvent();
			this.emitChangeEvent();
		},
		itemClicked (listItem)
		{
			const characteristic = listItem.id === ADD_NEW ? undefined : listItem;
			const freeText = listItem.id === ADD_NEW ? this.keyword : undefined;

			this.addItem({characteristic, freeText});
		},
		addItem ({characteristic, item, freeText})
		{
			this.$nextTick(() =>
			{
				this.keyword = '';

				if (this.$refs.autocomplete)
				{
					this.$refs.autocomplete.setText('');
					this.$refs.autocomplete.hideList();
				}
			});

			this.none = false;

			// if characteristic given: remove items which have the same free text as the given characteristic
			if (characteristic)
			{
				this.items = _.filter(
					this.items,
					item => isEmptyOrNull(item.freeText || '')
						|| !equals(item.freeText, characteristic.label)
				);
			}

			// find item if already in with this free text
			if (freeText)
			{
				item = _.find(
					this.items,
					item => equals(item.freeText || '', freeText)
						|| equals(item.item?.label || '', freeText.toLowerCase())
				);

				if (item) // already in
				{
					return;
				}
			}

			Event.push(
				new EventData()
					.setEvent(Event.CHARACTERISTIC_SELECTION)
					.setAction('Hinzugefügt (Software)')
					.setName(characteristic?.label ?? trim(freeText))
			);

			this.items.unshift({
				id: uuid.v4(),
				item: characteristic,
				freeText: trim(freeText),
				selfAssessment: undefined,
			});

			this.emitHasItemsEvent();
			this.emitChangeEvent();
		},
		emitHasItemsEvent ()
		{
			this.$emit('hasItems', arrayIsNotEmpty(this.items));
		},
		emitChangeEvent ()
		{
			if (!this.valid)
			{
				this.$emit('input', undefined);
				return;
			}

			const data = this.none ? [] : this.items;

			this.$emit('validChange', data);
			this.$emit('input', data);
		},
	}
}
</script>
